import type { Customer } from '../../@types/Customer.js';
import type { Product } from '../../@types/Product.js';
import { MiddlewarePaymentError } from '../../error/MiddlewarePaymentError.js';
import type { InteractionPrePaymentMiddlewareFn, InteractionSuccessProps, OnInteraction } from '../../flow/types.js';
import type { PreMiddlewareFn } from '../../payment/createPayment.js';
import { fetchMarketplaceBalance } from '../../utils/entry.js';
import { logger } from '../../utils/index.js';
import type { Wager } from '../../wager/Wager.js';

const calculateNewShares = async ({
  customer,
  product,
  drawNumber,
  activatedDrawId,
  wallets,
}: {
  customer: Customer;
  product?: Product;
  drawNumber?: number;
  activatedDrawId: number;
  wallets?: Record<number, { balance: number; sharePrice: number; soldShares: number }>;
}): Promise<{
  isChangedBalance: boolean;
  numberOfNewShares: number | undefined;
  balance: number | undefined;
  name: string | undefined;
}> => {
  let isChangedBalance = false;
  let numberOfNewShares;
  let balance;
  let name;

  const cachedWallet = wallets?.[activatedDrawId];
  if (!cachedWallet) {
    logger.debug('No cached wallet found for activatedDrawId', { activatedDrawId });
    return { isChangedBalance, numberOfNewShares, balance, name };
  }

  const data = await fetchMarketplaceBalance({
    activatedDrawId,
    product,
    drawNumber,
    customer,
    shouldForceFetch: true,
  });

  if (data && data.soldShares !== cachedWallet.soldShares) {
    isChangedBalance = true;
    numberOfNewShares = data.soldShares - cachedWallet.soldShares;
    balance = data.balance;
    name = data.name;
  }

  return { isChangedBalance, numberOfNewShares, balance, name };
};

type MarketplaceSharesSuccessInteraction = InteractionSuccessProps & {
  changedBalances: {
    balance: number;
    numberOfNewShares: number;
    name?: string;
  }[];
};

/**
 * Marketplace shares middleware interaction props.
 *
 * @param onInteraction The callback to trigger the interaction.
 * @param wallets The wallets object to access wallet information if needed.
 * @returns The middleware function.
 */
export function marketplaceSharesMw(
  onInteraction: OnInteraction<MarketplaceSharesSuccessInteraction>,
  wallets?: Record<number, { balance: number; sharePrice: number; soldShares: number }>
): PreMiddlewareFn<Wager> {
  const fn: ReturnType<InteractionPrePaymentMiddlewareFn<Wager>> =
    ({ payment }) =>
    async (next) => {
      try {
        const changedBalances: { numberOfNewShares: number; balance: number; name?: string }[] = [];

        for (const payable of payment.getPayableItems()) {
          const { activatedDrawId } = payable;
          if (!activatedDrawId) {
            continue;
          }
          const { bets, customer } = payable;
          const [bet] = bets;

          // eslint-disable-next-line no-await-in-loop
          const { isChangedBalance, numberOfNewShares, balance, name } = await calculateNewShares({
            customer,
            product: bet?.product,
            drawNumber: bet?.drawNumber,
            activatedDrawId,
            wallets,
          });
          if (isChangedBalance) {
            changedBalances.push({ numberOfNewShares: numberOfNewShares ?? 0, balance: balance ?? 0, name });
          }
        }

        if (changedBalances.length > 0) {
          // logger.info(`Notify updated balance. New balance: ${balance}, numNewShares: ${numberOfNewShares}`);
          await onInteraction({ changedBalances });
        }
        next();
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        const err = error instanceof Error ? error : new Error(`${error}`);
        logger.debug('Marketplace shares check failed', { message: err.message, error });

        const mwError = new MiddlewarePaymentError({ clientCode: 'PAYMENT_ABORTED_MP_SHARES' });
        next(mwError);
      }
    };
  return fn;
}
